import { VideoEditor } from '@src/editor';
import { selectKaraokeVariantEntryForLanguage } from '@src/editor/models/karaoke/selectKaraokeVariant';
import { applyCaptionsLanguage } from '@src/editor/services/applyCaptionsLanguage';
import { correctKaraokeDataFonts } from '@src/editor/services/captionFontSubstitution';
import { PublishSourceService } from '@src/services/publish/PublishSourceService';
import { normalizeLangKey } from '@src/utility/bcp47';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { FONTS } from '@src/utility/general';
import { LogManager } from '@src/utility/Logger';
import { projectCompositionTranslations } from '@src/videoTranscriptionProcessor/translation/projectCompositionTranslations';
import { selectLivePhrases } from '@src/videoTranscriptionProcessor/translation/selectLivePhrases';

import type { DubVoiceMode } from '@src/editor/dubbing/DubEngine';
import type { TranslationPhraseResolver } from '@src/editor/interface/IVideoEditor';
import type { KaraokeData } from '@src/editor/types/karaoke';
import type { DubManifestDTO } from '@src/repositories/DubbingRepository';
import type { Video } from '@src/types.ts/story';
import type { TranscriptData } from '@src/types.ts/transcript';
import type { VideoResolution, VideoSource } from '@src/types.ts/video';
import type { CompositionPhraseTranslation } from '@src/videoTranscriptionProcessor/translation/compositionTypes';

type ReplaceUrlFn = (
  source: VideoSource,
  resLevel: VideoResolution,
) => VideoSource;

/**
 * One published language's dub, resolved once per publish command.
 *
 * `error` is deliberately not folded into `none`: a fetch that failed for a
 * reason other than "no dub" leaves us not knowing whether a dub exists, so the
 * builder throws rather than guess, and the fan-out's per-cell catch tells the
 * person. Treating it as `none` would deliver the original recording under the
 * requested language and report success.
 */
export type PublishDubEntry =
  | {
      status: 'ready';
      manifest: DubManifestDTO;
      renditionUrls: Record<string, string>;
    }
  | { status: 'none' }
  | { status: 'error'; message: string };

/**
 * The persisted per-AR state one render source is built from — a populated AR
 * variant. `videoSource` / `transcriptElements` / `karaokeData` come from that
 * variant's resources; `originalTranscriptElements` is the story's original
 * transcript (the loadTranscript fallback).
 *
 * PLAT-10788 — `karaokeDataByLanguage` holds the persisted per-language caption
 * variants; `buildSource` loads the one matching the cell's language rather than
 * re-producing. `karaokeData` remains the single-blob fallback (legacy stories +
 * produce-on-miss for a language that was never edited).
 */
export interface PublishSourceBase {
  videoSource: Record<string, unknown>;
  transcriptElements: TranscriptData['elements'];
  originalTranscriptElements: TranscriptData['elements'];
  karaokeData?: KaraokeData | null;
  karaokeDataByLanguage?: Record<string, KaraokeData>;
  /**
   * PLAT-11410 — the canonical source-language key (from
   * `resolveSourceLanguageKey`), so the ORIGINAL cell's produce fallback can
   * font-correct a non-Latin source language (a Russian source video).
   */
  sourceLanguageKey?: string;
  extraElementData?: Video['extraElementData'];
  scenes?: Video['scenes'];
  originalVideoDuration?: string;
  /**
   * One entry per selected non-original language, keyed by
   * `normalizeLangKey(language)`. Absent means no fetch was made, which reads as
   * "no dub" and delivers the original recording.
   */
  dubsByLanguage?: Record<string, PublishDubEntry>;
  /**
   * The kept music bed's signed address for this story, or `null`.
   *
   * Handed in rather than read off the recipe, because the bed is a derived
   * value now (PLAT-11511): it is built into each document rather than stored
   * on the clip, so a render has to be given the address the same way the
   * screen is.
   */
  musicBedUrl?: string | null;
  /** The separated voice, for a lead-in, handed in the same way. */
  voiceStemUrl?: string | null;
}

/**
 * Builds a render source per `(aspectRatio × language)` in a throwaway headless
 * `VideoEditor` — the `AIClipProcessor` pattern — without touching the live
 * editor, so cells are independent and never disturb the user's session.
 *
 * Per language: karaoke uses the persisted per-language variant from
 * `karaokeDataByLanguage` when one exists (loaded as-is, no re-production);
 * otherwise it falls back to `applyCaptionsLanguage` (produce) on the single
 * `karaokeData` blob — for legacy stories or a language never edited (PLAT-10788,
 * DS10). Composition-overlay text is projected for the language (which the normal
 * publish path does not do). The live editor is never mutated.
 *
 * Deps are injected as functions (not the store) so the builder is unit
 * testable: `getPhrases(language)` supplies translation phrases to the karaoke
 * producer; `getCompositionLookup(language)` supplies the composition-phrase map;
 * `hydrateMediaDuration(source)` backfills `media_duration` on original video
 * elements (delegates to `src/utility/timeline.ts:hydrateOriginalMediaDuration`).
 */
export class PublishSourceBuilder {
  private logger = LogManager.getLogger('service:PublishSourceBuilder');
  private readonly publishSource = new PublishSourceService();

  constructor(
    private readonly getPhrases: TranslationPhraseResolver,
    private readonly getCompositionLookup: (
      language: string,
    ) => ReadonlyMap<string, CompositionPhraseTranslation>,
    private readonly hydrateMediaDuration: (source: VideoSource) => VideoSource,
    /**
     * "Is this the clip's original video?" — injected as a function, not a
     * store, because this builder must not import one.
     */
    private readonly isOriginalVideoElement: (probe: unknown) => boolean,
    /**
     * PLAT-11466 — the font a translated composition element should render
     * in, or `null` to keep its own. Optional so existing constructions keep
     * today's behaviour; the store adapter wires the workspace's brand-font
     * coverage in. Last because it is the only optional parameter.
     */
    private readonly resolveFontSubstitute?: (
      fontFamily: string,
      targetLanguage: string,
    ) => string | null,
  ) {}

  /**
   * Produce a render source for one cell: seed a fresh editor from `base`, apply
   * caption `language`, serialise at `resLevel` (URL swap via `replaceUrl`), and
   * project composition translations for the language.
   */
  buildSource(
    base: PublishSourceBase,
    language: string,
    resLevel: VideoResolution,
    replaceUrl: ReplaceUrlFn,
  ): VideoSource {
    const editor = this.seedEditor(base);

    // Runs before the caption work below only so it acts on the plainest
    // possible timeline; neither block reads what the other writes.
    this.applyCellDub(base, editor, language);

    // PLAT-10945 — captions burned into a published video must carry the live
    // wording only. A correction retires the phrases it replaces rather than
    // deleting them, and the producer resolves two phrases claiming the same
    // source word by array position, so a retired record could take those words
    // back. Filter at the injection point, exactly as the live editor's
    // `wireTranslationPhraseResolver` does, so the rule sits in one place.
    editor.setTranslationPhraseResolver(language =>
      selectLivePhrases(this.getPhrases(language)),
    );

    // PLAT-10788 (DS10) — prefer the persisted per-language variant; only
    // produce on a miss. An empty `{ clips: [] }` variant counts as a miss
    // (DS6a). If no karaoke was ever authored (`karaokeData` absent), do
    // nothing — don't produce captions automatically during publish.
    const entry = selectKaraokeVariantEntryForLanguage(
      base.karaokeDataByLanguage,
      { isOriginal: isOriginalLang(language), langCode: language },
    );
    if (entry && entry.variant.clips.length > 0) {
      // PLAT-11410 (epic DE5) — publish-time font safety net: the persisted
      // variant may predate the editor-side correction (PLAT-11409), or the
      // story was never reopened after it, so correct a COPY under the
      // variant's own canonical key before loading. Same known-family gate as
      // the hydrate correction — a deliberate brand-font pick is never fought
      // at publish either, and an already-correct variant comes back by
      // reference. Defense-in-depth: the fanout's `buildSourceBase` corrects
      // the whole record up front (fingerprint parity), so this is usually a
      // same-reference no-op.
      const mapVariant = correctKaraokeDataFonts(entry.variant, entry.key, {
        knownFamilies: FONTS,
      });
      // PLAT-10862 — the persisted variant may predate transcript edits made
      // while another language was active, so reconcile it against this build's
      // (current) transcript on load. The editor was just seeded with that
      // transcript and the live-phrase resolver above, so the reconcile — incl.
      // the translated adopt step — has everything it needs. Idempotent for a
      // fresh variant.
      editor.loadKaraokeData(mapVariant, { reconcileAgainstTranscript: true });
    } else if (base.karaokeData) {
      editor.loadKaraokeData(base.karaokeData);
      const applied = applyCaptionsLanguage(editor, language, undefined, {
        // PLAT-11410 — the canonical key so the produce-path font correction
        // (PLAT-11409) fires: translated cells use the cell language, the
        // original cell uses the base's source key (a Russian source video's
        // original captions correct too).
        targetLanguageKey: isOriginalLang(language)
          ? base.sourceLanguageKey
          : normalizeLangKey(language),
      });
      if (applied.kind === 'produce-failed' || applied.kind === 'exception') {
        this.logger.warn(
          'Karaoke production failed while building publish source',
          {
            language,
            outcome: applied.kind,
          },
        );
      }
    }

    let source = this.publishSource.prepareSourceForPublish(
      editor.timeline,
      resLevel,
      replaceUrl,
    );

    // Composition-overlay translation (the normal publish path skips this).
    // `projectCompositionTranslations` self-short-circuits for the original
    // language; guard here too so we don't build the lookup needlessly.
    if (!isOriginalLang(language)) {
      const projected = projectCompositionTranslations({
        videoSourceElements: source.elements as Record<string, unknown>[],
        extraElementData: base.extraElementData ?? {},
        activeLanguage: language,
        compositionPhrasesByNormalizedSource:
          this.getCompositionLookup(language),
        resolveFontSubstitute: this.resolveFontSubstitute,
      });
      if (projected.changed) {
        source = { ...source, elements: projected.elements };
      }
    }

    return source;
  }

  /**
   * Put this cell's language on the render, take every other language off, and
   * deliver it in the voice position the clip was saved in.
   *
   * **Language and position are read from different places, and that is the
   * point.** The language is always the cell's — the tick box the person used,
   * never the clip. The position (Original, Dubbed, Both) is always the clip's
   * saved composition, never the cell and never a position some one person's
   * screen happens to be holding: a delivered file's audio must not depend on
   * who pressed the button. Reading the position TOGETHER with the language it
   * was saved under is the failure this closes — a clip last saved in Russian
   * would ship Russian when Ukrainian was ticked, and report success.
   *
   * All of it happens on the throwaway headless editor `seedEditor` just built,
   * so the stored clip recipe is never written.
   *
   * It throws in exactly two situations, both of which fail this ONE cell
   * through the fan-out's per-cell catch rather than the whole publish: the
   * fetch for this language failed for a reason that is not "no dub", and the
   * command reported failure. Carrying on either way would deliver the wrong
   * language and report success.
   */
  private applyCellDub(
    base: PublishSourceBase,
    editor: VideoEditor,
    language: string,
  ): void {
    // The clip's saved voice position, read off the three stored options.
    //
    // The position, and ONLY the position. The stored language is used for one
    // thing here — telling "the clip has a saved position" from "it has none" —
    // and never to pick what is placed. What is placed is always this cell's
    // own language, so publishing Ukrainian on a clip last saved in Russian can
    // never deliver Russian.
    //
    // `null` means no saved position at all, and such a clip is published
    // dubbed: this cell's language, at full level, over a silenced picture.
    const saved = editor.timeline.dubOptions;
    const savedVoiceMode: DubVoiceMode =
      saved && saved.language !== null ? saved.voiceMode : 'dubbed';

    // The original cell arrives as the ORIGINAL_LANGUAGE sentinel and must
    // never be matched against a dub; the key is normalised on BOTH sides.
    const dubEntry: PublishDubEntry = isOriginalLang(language)
      ? { status: 'none' }
      : (base.dubsByLanguage?.[normalizeLangKey(language)] ?? {
          status: 'none',
        });

    if (dubEntry.status === 'error') {
      // We do not know whether this language has a dub, so we refuse to guess.
      throw new Error(
        `Cannot publish ${language}: its dub could not be read (${dubEntry.message})`,
      );
    }

    // Everything below hands the throwaway editor what the dub engine needs and
    // then leaves. The engine itself runs when the timeline is turned into the
    // render document, which is the same moment and the same code as the screen
    // preview (PLAT-11511) — so a render can no longer carry a dub built to a
    // different shape from the one the person heard.
    editor.dubSource.setOriginalVideoProbe(this.isOriginalVideoElement);
    if (dubEntry.status === 'ready') {
      editor.dubSource.remember(language, {
        manifest: dubEntry.manifest,
        renditionUrls: dubEntry.renditionUrls,
      });
    }
    editor.dubSource.setMusicBedUrl(base.musicBedUrl ?? null);
    editor.dubSource.setVoiceStemUrl(base.voiceStemUrl ?? null);

    // This cell's own language, at the clip's own position, with the clip's own
    // music choice. Written onto the throwaway editor only — the stored clip is
    // never touched.
    editor.timeline.setDubOptions({
      language: isOriginalLang(language) ? null : language,
      voiceMode: isOriginalLang(language) ? 'original' : savedVoiceMode,
      musicMode: saved?.musicMode ?? 'none',
      // The clip's level, whatever the cell's language (PLAT-11899): it is one
      // per clip, and dropping it here would publish every cell at full.
      voiceVolume: saved?.voiceVolume,
      // PLAT-11927 — the picture's level on Both, carried for the same reason.
      originalVolume: saved?.originalVolume,
      // Each language's own line levels; the dub pass reads this cell's.
      perLanguageConfiguration: saved?.perLanguageConfiguration,
      // The original lead-in on Both, so the render comes in late as heard.
      leadIn: saved?.leadIn,
    });

    this.logger.debug('Cell dub handed to the engine', {
      language,
      dubEntry: dubEntry.status,
      savedVoiceMode,
      musicMode: saved?.musicMode ?? 'none',
      hasMusicBed: !!base.musicBedUrl,
    });
  }

  private seedEditor(base: PublishSourceBase): VideoEditor {
    const editor = new VideoEditor();
    editor.setFlag('enableTranscriptMigrationValidation', false);
    // Extra element data must precede loadVideoSource (logo conversion reads it).
    editor.passExtraElementData(base.extraElementData ?? {});
    // Backfill media_duration on original video elements before loading into
    // the editor model. Delegates to the existing utility via the injected dep.
    const hydratedSource = this.hydrateMediaDuration(
      base.videoSource as VideoSource,
    );
    editor.loadVideoSource(hydratedSource, base.originalVideoDuration);
    editor.loadTranscript(
      base.transcriptElements,
      base.originalTranscriptElements,
    );
    // Karaoke is loaded per-language in `buildSource` (PLAT-10788), not here.
    // Scenes drive the LayoutEngine (positioning/merge) that toRenderJSON runs,
    // so load them for render parity with the live editor.
    if (base.scenes && base.scenes.length > 0) {
      editor.loadScenes(base.scenes);
    }
    return editor;
  }
}
